/**
 * Admin Booking Management System
 * Handles booking verification, group allocation, and logistics management
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  CheckCircleIcon,
  CheckBadgeIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  TruckIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  BanknotesIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';
import { operationalIntelligence } from '../../services/intelligenceService';
import { whatsappService } from '../../services/whatsappService';
import { Tour, Booking, Group, BookingPayment as Payment } from '../../types';
import { useToast } from '../../contexts/ToastContext';

export const BookingManagement: React.FC = () => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const { actualTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('pending');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [paymentHistory, setPaymentHistory] = useState<Payment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [tours, setTours] = useState<Tour[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualPayment, setManualPayment] = useState({
    amount: '',
    paymentMethod: 'cash',
    paymentType: 'installment' as 'deposit' | 'installment' | 'full_upfront' | 'final_payment',
    transactionReference: ''
  });
  const [isPrinting, setIsPrinting] = useState(false);

  // Auto-assign groups to vehicles
  const autoAssignGroups = async () => {
    try {
      const { autoAssignmentService } = await import('../../services/autoAssignmentService');
      const results = await autoAssignmentService.autoAssignNearCapacityGroups();

      if (results.length > 0) {
        const successCount = results.filter(r => r.success).length;
        console.log(`✅ Auto-assigned ${successCount}/${results.length} groups to vehicles`);

        // Reload groups to show updated assignments
        if (successCount > 0) {
          setTimeout(() => loadGroups(), 1000);
        }
      }
    } catch (error) {
      console.warn('Could not run auto-assignment:', error);
    }
  };

  const isSyncing = useRef(false);

  useEffect(() => {
    syncCommandCenter();
  }, [activeTab]);

  const syncCommandCenter = async () => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    setLoading(true);
    try {
      console.log('🔄 Syncing with Command Center (Sequential Mode)...');
      
      // 1. Fetch Bookings
      try {
        const bookingsData = await apiService.request<Booking[]>(`/bookings?status=${activeTab}`);
        setBookings(bookingsData || []);
      } catch (e) {
        console.error('❌ Failed to fetch bookings:', e);
      }

      // 2. Fetch Logistics/Groups
      try {
        const groupsData = await apiService.request<Group[]>('/logistics');
        setGroups(groupsData || []);
      } catch (e) {
        console.error('❌ Failed to fetch logistics:', e);
      }

      // 3. Fetch Tours
      try {
        const toursData = await apiService.request<Tour[]>('/tours');
        setTours(toursData || []);
      } catch (e) {
        console.error('❌ Failed to fetch tours:', e);
      }

      // Removed automatic autoAssignGroups() to prevent connection spikes.
      // Auto-assignment should be triggered manually via the UI.
    } catch (err) {
      console.error('❌ Global sync error:', err);
      setError('Partial or full failure syncing with command center');
    } finally {
      setLoading(false);
      isSyncing.current = false;
    }
  };

  const loadGroups = async () => {
    try {
      const allGroups = await apiService.request<Group[]>('/logistics');
      setGroups(allGroups || []);
    } catch (error: any) {
      console.error('❌ Error loading groups:', error);
    }
  };

  const fetchPaymentHistory = async (bookingId: string) => {
    setIsLoadingPayments(true);
    try {
      const history = await apiService.getPaymentHistory(bookingId);
      setPaymentHistory(history || []);
    } catch (error: any) {
      console.error('❌ Error fetching payment history:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleViewBooking = async (booking: Booking) => {
    setSelectedBooking(booking);
    setAdminNotes(booking.adminNotes || '');
    setShowBookingModal(true);
    await fetchPaymentHistory(booking.id);
  };

  const handleVerifyPayment = async (paymentId: string) => {
    try {
      await apiService.verifyPayment(paymentId);
      if (selectedBooking) {
        await fetchPaymentHistory(selectedBooking.id);
        await syncCommandCenter(); // Refresh booking status
        // WhatsApp: Notify guest that payment is verified
        whatsappService.notifyVerification(selectedBooking, selectedBooking.tourName || 'Your Safari');
      }
    } catch (error: any) {
      showError(`Failed to verify payment: ${error.message}`, 'Payment Error');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedBooking) return;
    try {
      if (!manualPayment.amount) return showWarning('Please enter an amount', 'Missing Amount');

      await apiService.recordPayment({
        bookingId: selectedBooking.id,
        amount: parseFloat(manualPayment.amount),
        currency: selectedBooking.currency || 'USD',
        paymentMethod: manualPayment.paymentMethod,
        paymentType: manualPayment.paymentType,
        transactionReference: manualPayment.transactionReference
      });

      setManualPayment({
        amount: '',
        paymentMethod: 'cash',
        paymentType: 'installment',
        transactionReference: ''
      });

      await fetchPaymentHistory(selectedBooking.id);
      await syncCommandCenter(); // Refresh booking status
    } catch (error: any) {
      showError(`Failed to record payment: ${error.message}`, 'Payment Error');
    }
  };

  const handleVerifyBooking = async (bookingId: string) => {
    try {
      await apiService.updateBookingStatus(bookingId, 'verified');
      setBookings(prev => prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: 'verified', verifiedAt: new Date().toISOString() }
          : booking
      ));
      console.log(`✅ Verified booking ${bookingId}`);

      // WhatsApp: Notify guest that booking is verified
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        whatsappService.notifyVerification(booking, booking.tourName || 'Your Safari');
      }
    } catch (error: any) {
      console.error('❌ Error verifying booking:', error);
      setError(error.message || 'Failed to verify booking');
    }
  };

  const handleRejectBooking = async (bookingId: string) => {
    try {
      await apiService.updateBookingStatus(bookingId, 'rejected');
      setBookings(prev => prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: 'rejected' }
          : booking
      ));
      console.log(`✅ Rejected booking ${bookingId}`);
    } catch (error: any) {
      console.error('❌ Error rejecting booking:', error);
      setError(error.message || 'Failed to reject booking');
    }
  };

  const handleAllocateToGroup = async (bookingId: string, groupId: string) => {
    try {
      // For now, use existing group allocation if available in apiService or implement it
      // Since it's a specific action, we might need a dedicated endpoint
      console.log(`Allocating booking ${bookingId} to group ${groupId}`);
      // Fallback to reload if not directly implemented
      await loadGroups();
    } catch (error: any) {
      console.error('❌ Error allocating booking to group:', error);
      setError(error.message || 'Failed to allocate booking to group');
    }
  };

  const handleCloseGroup = async (groupId: string) => {
    try {
      // For now, reload groups. In a real app, you'd call an API endpoint.
      console.log(`Closing group ${groupId}`);
      await loadGroups();
    } catch (error: any) {
      console.error('❌ Error closing group:', error);
    }
  };

  const handlePrintReceipt = async (bookingId: string) => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      await apiService.downloadReceipt(bookingId);
      showSuccess('Success', 'Official receipt generated and downloaded successfully');
    } catch (error: any) {
      console.error('❌ Error downloading receipt:', error);
      showError('Error', error.message || 'Failed to generate receipt. Please try again.');
    } finally {
      setIsPrinting(false);
    }
  };

  const getStatusColor = (status: string) => {

    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-amber-500/10 text-amber-500 border border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]';
      case 'verified': return 'bg-blue-500/10 text-blue-500 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]';
      case 'paid': return 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]';
      case 'grouped': return 'bg-purple-500/10 text-purple-500 border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]';
      case 'rejected': return 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]';
      default: return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
    }
  };

  const getGroupStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]';
      case 'filling': return 'bg-amber-500/10 text-amber-500 border border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]';
      case 'closed': return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500 border border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]';
      default: return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
    }
  };

  const filteredBookings = bookings.filter(booking => {
    // 1. Tab Filtering
    const matchesTab = (() => {
      switch (activeTab) {
        case 'pending': return booking.status === 'pending';
        case 'verified': return booking.status === 'verified';
        case 'paid': return booking.status === 'paid';
        case 'grouped': return booking.status === 'grouped';
        case 'rejected': return booking.status === 'rejected';
        default: return true;
      }
    })();

    if (!matchesTab) return false;

    // 2. Search Query Filtering
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const searchString = [
      booking.id,
      booking.customerName,
      booking.customerEmail,
      booking.tourName,
      booking.tourId
    ].filter(Boolean).join(' ').toLowerCase();

    return searchString.includes(query);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'}`}>
      {/* Error Message */}
      {error && (
        <div className={`rounded-2xl shadow-sm border p-4 transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-red-900/20 border-red-600/30 text-red-300'
          : 'bg-red-50 border-red-200 text-red-800'
          }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <p>{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && bookings.length === 0 && (
        <div className={`rounded-2xl shadow-sm border p-12 text-center transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-slate-800/50 border-slate-700'
          : 'bg-white border-neutral-200'
          }`}>
          <UserGroupIcon className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
          <h3 className="text-xl font-bold mb-2 uppercase tracking-tight">No Bookings Found</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-sm mx-auto mb-6">
            Our scanners detected no reservations in this sector. Refresh the system to check for new expedition requests.
          </p>
          <button
            onClick={() => syncCommandCenter()}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all active:scale-95"
          >
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            Refresh Roster
          </button>
        </div>
      )}
      {/* Header - Audit Intelligence */}
      <div className="flex flex-col gap-6 mb-10">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 group w-full">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search Guest Name, Email, or Booking ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-6 py-4 rounded-2xl border transition-all shadow-sm ${actualTheme === 'dark'
                ? 'bg-slate-800/50 border-slate-700 focus:bg-slate-800 focus:border-blue-500/50 text-white'
                : 'bg-white border-neutral-200 focus:border-blue-400 focus:shadow-lg focus:shadow-blue-500/5'
                }`}
            />
          </div>
          
          {/* Quick Filters - Horizontal Scroll */}
          <div className="relative group w-full md:w-auto -mx-4 sm:mx-0">
            <div className="flex flex-nowrap-forced items-center gap-2 overflow-x-auto scrollbar-hide pb-2 px-4 md:px-0" style={{ flexWrap: 'nowrap' }}>
              {['all', 'pending', 'verified', 'paid', 'grouped'].map((tabId) => (
                <button
                  key={tabId}
                  onClick={() => setActiveTab(tabId)}
                  className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex-shrink-0 whitespace-nowrap shadow-sm ${activeTab === tabId
                    ? 'bg-blue-600 text-white ring-2 ring-blue-100 ring-offset-2 dark:ring-offset-slate-900'
                    : `border ${actualTheme === 'dark' ? 'bg-slate-800/80 border-slate-700 text-slate-400 hover:text-slate-200' : 'bg-white border-neutral-200 text-gray-500 hover:border-neutral-300'}`
                    }`}
                >
                  {tabId}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Manifest', value: bookings.length, icon: ChartBarIcon, color: 'blue' },
            { label: 'Pending Audit', value: bookings.filter(b => b.status === 'pending').length, icon: ClockIcon, color: 'amber' },
            { label: 'Verified Flow', value: bookings.filter(b => b.status === 'verified').length, icon: CheckBadgeIcon, color: 'emerald' },
            { label: 'Global Rev (KES)', value: bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0).toLocaleString(), icon: BanknotesIcon, color: 'indigo' },
          ].map((stat) => (
            <div key={stat.label} className={`p-5 rounded-2xl border transition-all duration-300 hover:shadow-md ${actualTheme === 'dark' ? 'bg-slate-800/30 border-slate-700' : 'bg-white border-neutral-100 shadow-sm'}`}>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 opacity-60">{stat.label}</p>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-black tracking-tight">{stat.value}</p>
                <div className={`p-2 rounded-lg bg-${stat.color}-500/10`}>
                  <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Navigation Tabs - Premium Scrollable Chips */}
      <div className="relative group mb-10 -mx-4 sm:mx-0">
        <div className={`flex flex-nowrap-forced overflow-x-auto scrollbar-hide gap-2 p-1.5 px-4 sm:px-1.5 relative z-10 ${actualTheme === 'dark'
          ? 'border-b border-slate-800'
          : 'border-b border-neutral-100'
          }`} style={{ flexWrap: 'nowrap' }}>
          {[
            { id: 'all', name: 'Master Roster', count: bookings.length },
            { id: 'pending', name: 'Awaiting Audit', count: bookings.filter(b => b.status === 'pending').length },
            { id: 'verified', name: 'Verified Clear', count: bookings.filter(b => b.status === 'verified').length },
            { id: 'paid', name: 'Financial Clear', count: bookings.filter(b => b.status === 'paid').length },
            { id: 'grouped', name: 'Ready to Launch', count: bookings.filter(b => b.status === 'grouped').length },
            { id: 'rejected', name: 'Rejected/Risk', count: bookings.filter(b => b.status === 'rejected').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex-shrink-0 whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5 ${activeTab === tab.id
                ? 'bg-blue-600 text-white ring-2 ring-blue-100 ring-offset-2 dark:ring-offset-slate-900'
                : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border border-neutral-200 dark:border-slate-700 hover:border-blue-400 hover:text-blue-600'
                }`}
            >
              <div className="flex items-center gap-3">
                {tab.name}
                <span className={`px-2 py-0.5 rounded-lg text-[9px] font-bold ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-neutral-100 dark:bg-slate-700 text-gray-500'}`}>
                  {tab.count}
                </span>
              </div>
            </button>
          ))}
        </div>
        
        {/* Mobile scroll indicator fades */}
        <div className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l pointer-events-none sm:hidden z-20 ${
          actualTheme === 'dark' ? 'from-slate-900 via-slate-900/80 to-transparent' : 'from-neutral-50 via-neutral-50/80 to-transparent'
        }`} />
      </div>

      {/* Header */}
      <div className={`rounded-[2rem] border p-8 mb-8 shadow-sm transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-slate-800/50 border-slate-700'
        : 'bg-white border-neutral-200'
        }`}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CalendarDaysIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-3xl font-black uppercase tracking-tighter">
                Booking <span className="text-blue-600">Operations</span>
              </h2>
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium max-w-xl">
              Monitor, verify, and synchronize all guest expeditions within the Maasai SightTrails network.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <button
              onClick={() => {
                syncCommandCenter();
                loadGroups();
              }}
              className="flex items-center gap-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all active:scale-95 group"
            >
              <ArrowPathIcon className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
              <span>Sync Systems</span>
            </button>

            <div className="h-12 w-px bg-neutral-200 dark:bg-slate-700 hidden lg:block" />

            <div className="flex items-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-black leading-none mb-1">{bookings.length}</div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Bookings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black leading-none mb-1">{groups.length}</div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Active Groups</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings List */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pb-20">
        {filteredBookings.length === 0 ? (
          <div className={`col-span-full rounded-[2rem] border p-12 text-center transition-all duration-300 ${actualTheme === 'dark'
            ? 'bg-slate-800/50 border-slate-700'
            : 'bg-white border-neutral-200'
            }`}>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              No {activeTab} bookings detected in current stream
            </p>
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <div
              key={booking.id}
              onClick={() => handleViewBooking(booking)}
              className={`group relative border rounded-2xl p-4 transition-all duration-300 cursor-pointer overflow-hidden ${actualTheme === 'dark'
                ? 'bg-slate-800/50 border-slate-700 hover:border-blue-500/50 hover:bg-slate-800'
                : 'bg-white border-neutral-200 hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/5'
                }`}
            >
              <div className="flex items-center gap-4">
                {/* Compact Identifier */}
                <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center border transition-colors ${actualTheme === 'dark'
                  ? 'bg-slate-900/50 border-slate-700 group-hover:bg-blue-900/20 group-hover:border-blue-500/30'
                  : 'bg-blue-50 border-blue-100 group-hover:bg-blue-100 group-hover:border-blue-200'
                  }`}>
                  <CalendarDaysIcon className="w-5 h-5 text-blue-600 mb-0.5" />
                  <span className="text-[8px] font-black text-blue-600 uppercase tracking-tighter text-center leading-none">
                    {new Date(booking.startDate || booking.tourDate || '').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>

                {/* Compact Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-black uppercase tracking-tight truncate group-hover:text-blue-600 transition-colors">
                        {booking.tourName || 'Undefined Expedition'} — {booking.payment_method || 'Pending Method'}
                      </h3>
                      <p className="text-[10px] font-bold text-gray-500 truncate mt-0.5">
                        {booking.customerName} • {booking.customerEmail}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-0.5 text-[8px] font-black uppercase tracking-widest rounded-lg border ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50 dark:border-slate-700/50">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-[7px] font-black text-gray-400 uppercase tracking-widest">Revenue</p>
                        <p className="text-[11px] font-black">{booking.currency} {booking.totalAmount?.toLocaleString()}</p>
                      </div>
                      <div className="w-px h-6 bg-slate-100 dark:bg-slate-700" />
                      <div>
                        <p className="text-[7px] font-black text-emerald-600/50 uppercase tracking-widest">Verified</p>
                        <p className="text-[11px] font-black text-emerald-600">{booking.currency} {(booking.totalPaid || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded-lg">
                      <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
                        {(booking.pax?.adults || 0) + (booking.pax?.children || 0)} PAX
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          ))
        )}
      </div>

      {/* Active Expedition Groups */}
      < div className={`rounded-[2rem] border p-8 shadow-sm relative overflow-hidden group mb-20 transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-slate-800/50 border-slate-700'
        : 'bg-white border-neutral-200'
        }`}>
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl text-blue-600">
              <UserGroupIcon className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-2xl font-black uppercase tracking-tight">Active Expedition Groups</h3>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Real-time capacity and logistics distribution</p>
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="text-center py-12 bg-neutral-50 dark:bg-slate-900/30 border border-dashed border-neutral-200 dark:border-slate-700 rounded-[2rem]">
              <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-neutral-300 dark:text-slate-700" />
              <p className="text-gray-500 dark:text-gray-500 font-bold uppercase tracking-widest text-[9px]">Scan complete: No active groups found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <div key={group.id} className={`relative border rounded-3xl p-6 transition-all duration-300 ${actualTheme === 'dark'
                  ? 'bg-slate-900/50 border-slate-700 hover:border-blue-500/30'
                  : 'bg-white border-neutral-200 hover:shadow-lg hover:border-blue-200'
                  }`}>
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold uppercase tracking-tight truncate pr-4">{group.name}</h4>
                    <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-widest rounded-full border ${getGroupStatusColor(group.status)}`}>
                      {group.status}
                    </span>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Capacity</span>
                      <span className="text-xs font-bold">{group.currentBookings} / {group.maxCapacity}</span>
                    </div>
                    <div className="w-full h-1.5 bg-neutral-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-1000 shadow-[0_0_8px_rgba(37,99,235,0.3)]"
                        style={{ width: `${(group.currentBookings / group.maxCapacity) * 100}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-600/10">
                      Logistics
                    </button>
                    <button
                      onClick={() => handleCloseGroup(group.id)}
                      className="p-3 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 relative z-10 mt-8 pt-8 border-t border-neutral-100 dark:border-slate-700">
            <button
              onClick={() => groups.length > 0 && handleCloseGroup(groups[0].id)}
              className="flex-1 py-3 px-4 bg-red-50 text-red-600 hover:bg-red-100 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95"
            >
              Close Current Group
            </button>
            <button className="flex-1 py-3 px-4 bg-blue-50 text-blue-600 hover:bg-blue-100 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95">
              Command Intel
            </button>
          </div>
        </div>
      </div >

      {/* Booking Intelligence Modal */}
      {
        showBookingModal && selectedBooking && (
          <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-md flex items-center justify-center p-4 z-[100] animate-in fade-in duration-500">
            <div className={`rounded-[2.5rem] border shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar relative transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-slate-900 border-slate-700'
              : 'bg-white border-neutral-200'
              }`}>
              <div className="p-8 md:p-12">
                <div className="flex items-start justify-between mb-12">
                  <div className="flex items-center gap-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-blue-600 text-white rounded-2xl shadow-xl shadow-blue-600/20">
                      <EyeIcon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-black uppercase tracking-tighter mb-1">Expedition Intelligence</h3>
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">NODE::{selectedBooking.id.split('-')[0].toUpperCase()}</span>
                        <span className={`px-3 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full border ${getStatusColor(selectedBooking.status)}`}>
                          {selectedBooking.status}
                        </span>
                        {selectedBooking.payment_method && (
                          <span className="px-3 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full border bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800">
                            {selectedBooking.payment_method}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="p-3 bg-neutral-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 rounded-xl hover:bg-neutral-200 dark:hover:bg-slate-700 transition-all active:scale-95"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
                  {/* Main Info */}
                  <div className="lg:col-span-4 space-y-6">
                    <div className={`p-6 rounded-3xl border ${actualTheme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-blue-50/50 border-blue-100'}`}>
                      <h4 className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-4">Target Expedition</h4>
                      <p className="text-xl font-bold uppercase tracking-tight mb-4 leading-tight">{selectedBooking.tourName}</p>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <CalendarDaysIcon className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Safari Start</p>
                            <p className="text-xs font-bold">
                              {selectedBooking.tourDate 
                                ? new Date(selectedBooking.tourDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                            </p>
                          </div>
                        </div>
                        {selectedBooking.arrivalDate && (
                          <div className="flex items-center gap-3 p-3 bg-blue-100/30 rounded-2xl border border-blue-100/50">
                            <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                            <div>
                              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Arrival Window</p>
                              <p className="text-xs font-bold text-blue-900">
                                {new Date(selectedBooking.arrivalDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Financials */}
                  <div className={`lg:col-span-8 p-8 rounded-3xl border ${actualTheme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-neutral-50 border-neutral-200'}`}>
                    <div className="flex items-center justify-between mb-8">
                      <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Financial Transparency Audit</h4>
                      <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-lg border border-blue-100 dark:border-blue-800/30">
                        {selectedBooking.paymentPlan || 'FULL'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Total Payload</p>
                        <p className="text-2xl font-black">{selectedBooking.currency} {selectedBooking.totalAmount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Verified Credits</p>
                        <p className="text-2xl font-black text-emerald-600">{selectedBooking.currency} {(selectedBooking.totalPaid || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Outstanding</p>
                        <p className="text-2xl font-black text-blue-600">{selectedBooking.currency} {((selectedBooking.totalAmount || 0) - (selectedBooking.totalPaid || 0)).toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Price Breakdown ("Minimum Calculations") */}
                    <div className={`pt-6 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'}`}>
                      <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Calculation Breakdown</h5>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-gray-500 uppercase">Base Tour Price</span>
                          <span className="text-sm font-black">{selectedBooking.currency} {selectedBooking.basePrice?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-gray-500 uppercase">Selected Enhancements (Extras)</span>
                          <span className="text-sm font-black text-blue-600">
                            +{selectedBooking.currency} {(() => {
                              const extras = (selectedBooking as any).extras || selectedBooking.selectedExtras || [];
                              const total = extras.reduce((sum: number, e: any) => sum + Number(e.extraPrice || e.price || 0), 0);
                              return Number(selectedBooking.extrasTotal || total).toLocaleString();
                            })()}
                          </span>
                        </div>
                        {(() => {
                          const extrasTotalRaw = ((selectedBooking as any).extras || selectedBooking.selectedExtras || []).reduce((sum: number, e: any) => sum + Number(e.extraPrice || e.price || 0), 0);
                          const extrasTotalNum = Number(selectedBooking.extrasTotal || extrasTotalRaw || 0);
                          const basePriceNum = Number(selectedBooking.basePrice || 0);
                          const taxesTotalNum = Number(selectedBooking.taxesTotal || 0);
                          const totalAmountNum = Number(selectedBooking.totalAmount || 0);
                          
                          const adjustment = totalAmountNum - (basePriceNum + extrasTotalNum + taxesTotalNum);
                          
                          if (Math.abs(adjustment) > 1) {
                            return (
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-gray-500 uppercase">Seasonal Adjustment</span>
                                <span className={`text-sm font-black ${adjustment < 0 ? 'text-emerald-600' : 'text-orange-500'}`}>
                                  {adjustment > 0 ? '+' : ''}{selectedBooking.currency} {adjustment.toLocaleString()}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {selectedBooking.taxesTotal ? (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-gray-500 uppercase">Regulatory Taxes & Fees</span>
                            <span className="text-sm font-black text-slate-400">+{selectedBooking.currency} {selectedBooking.taxesTotal?.toLocaleString()}</span>
                          </div>
                        ) : null}
                        <div className={`pt-3 border-t flex justify-between items-center ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'}`}>
                          <span className="text-xs font-black uppercase text-slate-900 dark:text-white underline decoration-blue-500 decoration-2">Net Sum Total</span>
                          <span className="text-lg font-black">{selectedBooking.currency} {selectedBooking.totalAmount?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Ledger & Approvals */}
                    <div className={`mt-8 pt-6 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'}`}>
                      <div className="flex items-center justify-between mb-6">
                        <h5 className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-2">
                          <BanknotesIcon className="w-4 h-4" />
                          Payment Ledger & Approvals
                        </h5>
                        <button
                          onClick={() => {
                            const form = document.getElementById('record-payment-form');
                            if (form) form.classList.toggle('hidden');
                          }}
                          className="text-[9px] font-bold text-blue-600 uppercase tracking-widest bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          + Record Payment
                        </button>
                      </div>

                      {/* Record Payment Form (Hidden by default) */}
                      <div id="record-payment-form" className="hidden mb-6 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div>
                            <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Amount ({selectedBooking.currency})</label>
                            <input
                              type="number"
                              value={manualPayment.amount}
                              onChange={e => setManualPayment({ ...manualPayment, amount: e.target.value })}
                              className={`w-full px-3 py-2 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/50 outline-none transition-all ${actualTheme === 'dark' ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-neutral-200'}`}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Type</label>
                            <select
                              value={manualPayment.paymentType}
                              onChange={e => setManualPayment({ ...manualPayment, paymentType: e.target.value as any })}
                              className={`w-full px-3 py-2 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/50 outline-none transition-all ${actualTheme === 'dark' ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-neutral-200'}`}
                            >
                              <option value="deposit">Deposit</option>
                              <option value="installment">Installment</option>
                              <option value="full_upfront">Full Upfront</option>
                              <option value="final_payment">Final Payment</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Method</label>
                            <select
                              value={manualPayment.paymentMethod}
                              onChange={e => setManualPayment({ ...manualPayment, paymentMethod: e.target.value })}
                              className={`w-full px-3 py-2 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/50 outline-none transition-all ${actualTheme === 'dark' ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-neutral-200'}`}
                            >
                              <option value="cash">Cash</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="mpesa">M-Pesa</option>
                              <option value="card">Credit Card</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Ref (Optional)</label>
                            <input
                              type="text"
                              value={manualPayment.transactionReference}
                              onChange={e => setManualPayment({ ...manualPayment, transactionReference: e.target.value })}
                              className={`w-full px-3 py-2 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/50 outline-none transition-all ${actualTheme === 'dark' ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-neutral-200'}`}
                              placeholder="TXN..."
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            handleRecordPayment();
                            const form = document.getElementById('record-payment-form');
                            if (form) form.classList.add('hidden');
                          }}
                          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors shadow-lg shadow-blue-500/20"
                        >
                          Submit Payment Record
                        </button>
                      </div>

                      {/* Historical Ledger */}
                      {isLoadingPayments ? (
                        <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div></div>
                      ) : paymentHistory.length === 0 ? (
                        <div className="text-center py-6 border border-dashed rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">No payments recorded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto no-scrollbar pr-2">
                          {paymentHistory.map((payment) => (
                            <div key={payment.id} className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${actualTheme === 'dark' ? 'bg-slate-800/30 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-100 hover:border-blue-100'}`}>
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${payment.status === 'verified' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'}`}>
                                  {payment.status === 'verified' ? <CheckBadgeIcon className="w-5 h-5" /> : <ClockIcon className="w-5 h-5" />}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-black">{payment.currency} {payment.amount.toLocaleString()}</p>
                                    <span className={`px-2 py-0.5 text-[8px] font-black uppercase tracking-widest rounded-md ${payment.status === 'verified' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'}`}>
                                      {payment.status}
                                    </span>
                                  </div>
                                  <div className="flex flex-col gap-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                    <div className="flex items-center gap-2">
                                      <span className="text-blue-500 font-black">Method:</span>
                                      <span>{payment.paymentMethod || 'M-Pesa'}</span>
                                      {payment.transaction_reference && (
                                        <>
                                          <span className="mx-1">•</span>
                                          <span className="text-slate-600 dark:text-slate-300">Ref: {payment.transaction_reference}</span>
                                        </>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                      <div className="flex items-center gap-1">
                                        <ClockIcon className="w-3 h-3" />
                                        <span>Paid: {payment.created_at ? new Date(payment.created_at).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                                      </div>
                                      {payment.status === 'verified' && payment.verified_at && (
                                        <div className="flex items-center gap-1 text-emerald-600">
                                          <CheckBadgeIcon className="w-3 h-3" />
                                          <span>Verified: {new Date(payment.verified_at).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {payment.status !== 'verified' && (
                                <button
                                  onClick={() => handleVerifyPayment(payment.id)}
                                  className="px-4 py-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white dark:hover:text-white transition-all shadow-md"
                                >
                                  Verify
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Logistics & Enhancements */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className={`p-8 rounded-3xl border ${actualTheme === 'dark' ? 'bg-slate-800/30 border-slate-700' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-6 border-b border-blue-50 pb-4 text-left">Safari Enhancements</h4>
                    <div className="space-y-4">
                      {((selectedBooking as any).extras || selectedBooking.selectedExtras)?.length > 0 ? (() => {
                        const extrasList = (selectedBooking as any).extras || selectedBooking.selectedExtras;
                        const rawExtrasSum = extrasList.reduce((sum: number, e: any) => sum + Number(e.extraPrice || e.price || 0), 0);
                        const extrasTotal = Number(selectedBooking.extrasTotal || 0);
                        const needsConversionFix = selectedBooking.currency !== 'USD' && rawExtrasSum > 0 && (extrasTotal / rawExtrasSum > 50);

                        return extrasList.map((extra: any, i: number) => {
                          const rawPrice = Number(extra.extraPrice || extra.price || 0);
                          const displayPrice = needsConversionFix ? (rawPrice / rawExtrasSum) * extrasTotal : rawPrice;
                          return (
                            <div key={i} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                              <div className="flex items-center gap-3">
                                <span className="text-xl">{extra.icon || '✨'}</span>
                                <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{extra.extraName || extra.name}</span>
                              </div>
                              <span className="text-xs font-black text-blue-600 px-3 py-1 bg-white dark:bg-slate-900 rounded-lg shadow-sm">
                                {selectedBooking.currency} {displayPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                          );
                        });
                      })() : (
                        <div className="py-6 text-center text-gray-400 text-xs font-medium italic">No additional enhancements selected</div>
                      )}
                    </div>
                  </div>

                  <div className={`p-8 rounded-3xl border ${actualTheme === 'dark' ? 'bg-slate-800/30 border-slate-700' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-6 border-b border-blue-50 pb-4 text-left">Primary Contact Intel</h4>
                    {selectedBooking.leadGuestDetails ? (
                      <div className="space-y-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-xl font-black">
                            {selectedBooking.leadGuestDetails.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest text-left">Lead Expeditionist</p>
                            <p className="text-lg font-black text-slate-900 dark:text-white uppercase text-left">{selectedBooking.leadGuestDetails.name}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1 text-left">Direct Secure Line</p>
                            <p className="text-xs font-bold text-blue-600 truncate text-left">{selectedBooking.leadGuestDetails.phone}</p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1 text-left">Encrypted Email</p>
                            <p className="text-xs font-bold text-blue-600 truncate text-left">{selectedBooking.leadGuestDetails.email}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="py-10 text-center">
                        <p className="text-sm font-black text-slate-900 uppercase mb-1">{selectedBooking.customerName}</p>
                        <p className="text-xs text-slate-500">{selectedBooking.customerEmail}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Party Roster */}
                <div className="mb-12">
                  <div className="flex items-center gap-2 mb-6">
                    <UserGroupIcon className="w-5 h-5 text-blue-600" />
                    <h4 className="text-[10px] font-black uppercase tracking-widest">Expedition Party Roster</h4>
                  </div>
                  <div className="grid gap-3">
                    {Array.isArray(selectedBooking.participants) ? selectedBooking.participants.map((p, i) => (
                      <div key={p.id || i} className={`rounded-2xl border p-5 flex flex-col md:flex-row justify-between items-center gap-4 transition-colors ${actualTheme === 'dark' ? 'bg-slate-800/30 border-slate-700 hover:bg-slate-800' : 'bg-white border-neutral-100 hover:border-blue-100 hover:bg-blue-50/20'}`}>
                        <div className="flex items-center gap-4 w-full md:w-auto">
                          <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center text-blue-600 font-black italic">{i + 1}</div>
                          <div>
                            <h5 className="font-bold uppercase tracking-tight">{p.firstName} {p.lastName}</h5>
                            <div className="flex items-center gap-3">
                              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{p.nationality} • {p.age} YRS</span>
                              <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-tighter ${p.category === 'child' ? 'bg-orange-100 text-orange-600 border border-orange-200' : 'bg-blue-100 text-blue-600 border border-blue-200'}`}>
                                {p.category || 'ADULT'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-4 text-[10px] font-bold text-gray-400 w-full md:w-auto">
                          <span className="flex items-center gap-2"><EnvelopeIcon className="w-4 h-4 text-blue-600" /> {p.email}</span>
                          <span className="flex items-center gap-2"><PhoneIcon className="w-4 h-4 text-blue-600" /> {p.phone}</span>
                        </div>
                      </div>
                    )) : null}
                  </div>
                </div>

                {/* Action Bar */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-8 border-t border-neutral-100 dark:border-slate-800">
                  <button
                    onClick={() => handlePrintReceipt(selectedBooking.id)}
                    disabled={isPrinting}
                    className={`flex items-center justify-center gap-2 px-6 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-lg ${isPrinting 
                      ? 'bg-slate-400 cursor-not-allowed opacity-70' 
                      : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white dark:hover:text-white active:scale-95'
                    }`}
                  >
                    {isPrinting ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        GENERATING...
                      </>
                    ) : (
                      <>
                        <PrinterIcon className="w-4 h-4" />
                        Print Official Receipt
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="px-8 py-3.5 bg-neutral-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-neutral-200 dark:hover:bg-slate-700 transition-all"
                  >
                    Close Intel
                  </button>
                  {selectedBooking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleRejectBooking(selectedBooking.id)}
                        className="px-8 py-3.5 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-red-100 transition-all"
                      >
                        Reject Risk
                      </button>
                      <button
                        onClick={() => handleVerifyBooking(selectedBooking.id)}
                        className="px-8 py-3.5 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all"
                      >
                        Verify Expedition
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};